import {
  legacyReminderOccurrence,
  liveOnly,
  OpLogKind,
  parseLegacyReminderKey,
  parseReminderKey,
  reminderKey,
  personalDateOf,
  personalDateOfLocal,
  ReminderDecision,
  snoozeReminders,
  type ReminderItem,
  type ReminderPlan,
} from '@wiztime/core';
import { queries, type Store } from '@wiztime/storage';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { dayPath } from '../../app/routes';
import { useRunCommand } from '../../hooks/useRunCommand';
import { useServices } from '../../hooks/useServices';
import { useUndoToast } from '../../hooks/useUndoToast';
import { StorageKey } from '../../lib/storageKeys';
import { notifyPermission, NotifyPermission, showReminderNotice } from './notifier';
import { readStoredReminderState, useReminderPrefs, writeStoredReminderState } from './reminderPrefs';
import {
  readReminderMessage,
  readReminderQuery,
  REMINDER_KEYS_QUERY,
  REMINDER_QUERY,
  ReminderAction,
  type ReminderCommand,
} from './reminderProtocol';
import { createReminderRunner, planAt, type ReminderRunner, type ReminderRunnerDeps } from './reminderRunner';
import { noticesOf } from './reminderText';

type StorageDeps = Pick<ReminderRunnerDeps, 'loadSettings' | 'load' | 'readState'>;

/** Storage reads of the reminder loop. */
export function reminderStorageDeps(store: Store): StorageDeps {
  return {
    loadSettings: async () => {
      const settings = await queries.settings(store.repo);
      return {
        dayStartMin: settings.dayStartMin,
        legacyDayStartMin: settings.checkinLegacyDayStartMin,
        reminderScope: settings.reminderScope,
        reminderLeadMin: settings.reminderLeadMin,
      };
    },
    load: async (dates) => {
      const items: ReminderItem[] = liveOnly(await queries.checkinItems(store.repo));
      const sorted = [...dates].sort();
      const first = sorted[0]!;
      const last = sorted[sorted.length - 1]!;
      // One range query over the dates with the one-day margin on both sides.
      const days = Math.max(0, Math.round((Date.parse(last) - Date.parse(first)) / 86_400_000)) + 1;
      const records = await queries.recentCheckinLogs(store.repo, last, days);
      const logs = liveOnly(records).map((log) => ({ ...log.data, hlc: log.hlc }));
      return { items, logs };
    },
    readState: readStoredReminderState,
  };
}

/** Record a reminder decision in the operation log (plan §5); a failed write never stops reminders. */
export function logReminderDecision(store: Store, decision: ReminderDecision, keys: readonly string[]): void {
  if (keys.length === 0) return;
  void store.logOperation({ kind: OpLogKind.Reminder, name: decision, summary: { keys: [...keys] } }).catch((error: unknown) => {
    console.warn('[reminder] operation log write failed', error);
  });
}

/** The decisions of one check: on-time notices shown, missed ones summarised. */
function logPlan(store: Store, plan: ReminderPlan, failed: readonly string[]): void {
  const notShown = new Set(failed);
  logReminderDecision(
    store,
    ReminderDecision.Shown,
    plan.onTime.flat().flatMap((slot) => (notShown.has(slot.key) ? [] : [slot.key])),
  );
  logReminderDecision(
    store,
    ReminderDecision.Missed,
    plan.missed.flatMap((slot) => (notShown.has(slot.key) ? [] : [slot.key])),
  );
}

/**
 * Item and occurrence of each notification key; a pre-upgrade key (`personalDate|itemId`) is
 * mapped to that day's first timed occurrence of the item. Unknown items are dropped.
 */
export async function reminderTargets(
  store: Store,
  keys: readonly string[],
): Promise<{ readonly key: string; readonly itemId: string; readonly occursAt: string }[]> {
  const current = keys.flatMap((key) => {
    const parsed = parseReminderKey(key);
    return parsed === null ? [] : [{ key, ...parsed }];
  });
  const legacy = keys.flatMap((key) => {
    const parsed = parseLegacyReminderKey(key);
    return parsed === null ? [] : [parsed];
  });
  if (legacy.length === 0) return current;
  const settings = await queries.settings(store.repo);
  const items = liveOnly(await queries.checkinItems(store.repo));
  const mapped = legacy.flatMap(({ itemId, personalDate }) => {
    const item = items.find((entry) => entry.id === itemId);
    const occursAt =
      item === undefined
        ? null
        : legacyReminderOccurrence(item, personalDate, {
            dayStartMin: settings.dayStartMin,
            legacyDayStartMin: settings.checkinLegacyDayStartMin,
          });
    return occursAt === null ? [] : [{ key: reminderKey(itemId, occursAt), itemId, occursAt }];
  });
  return [...current, ...mapped];
}

/** Name of the Web Lock that serialises reminder checks and snoozes across the tabs of a browser. */
const REMINDER_LOCK = 'wiztime-reminder';

/** Run `body` under the reminder lock; without the Web Locks API (older browsers) it runs directly. */
export function withReminderLock(body: () => Promise<void>): Promise<void> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  return locks === undefined ? body() : locks.request(REMINDER_LOCK, body);
}

/**
 * Mark every reminder that is already due as shown, without showing it. Called when the user
 * turns reminders on, so the first check does not summarise the whole day as missed.
 */
export async function skipDueReminders(store: Store, now: number): Promise<void> {
  await withReminderLock(async () => {
    const plan = await planAt(reminderStorageDeps(store), now);
    writeStoredReminderState(plan.state);
    logReminderDecision(store, ReminderDecision.Silent, [...plan.onTime.flat(), ...plan.missed].map((slot) => slot.key));
  });
}

/**
 * Check-in reminders of this page (docs/requirements/checkin-reminder.md): runs the reminder loop
 * while the device switch is on, and carries out the notification buttons (done, later, open),
 * which arrive as service-worker messages or, when the click had to open a new window, in the
 * start-up URL. Mounted once, in the app shell.
 */
export function useCheckinReminders(): void {
  const { store, now } = useServices();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const run = useRunCommand();
  const showUndo = useUndoToast();
  const enabled = useReminderPrefs((prefs) => prefs.enabled);
  const runnerRef = useRef<ReminderRunner | null>(null);

  // The command handler changes with the language and the router; the listeners read it from a ref.
  const handleRef = useRef<(command: ReminderCommand) => Promise<void>>(() => Promise.resolve());
  const handle = async (command: ReminderCommand): Promise<void> => {
    const targets = await reminderTargets(store, command.keys);
    if (command.action === ReminderAction.Snooze) {
      // By occurrence key: a notification shown before the upgrade carries `personalDate|itemId`.
      const keys = targets.map((target) => target.key);
      await withReminderLock(async () => {
        writeStoredReminderState(snoozeReminders(readStoredReminderState(), keys, now()));
      });
      logReminderDecision(store, ReminderDecision.Snoozed, keys);
      await runnerRef.current?.check();
      return;
    }
    if (command.action === ReminderAction.Open) {
      const settings = await queries.settings(store.repo);
      const first = targets[0];
      navigate(
        dayPath(
          first === undefined
            ? personalDateOf(now(), settings.dayStartMin)
            : personalDateOfLocal(first.occursAt, settings.dayStartMin),
        ),
      );
      return;
    }
    const names: string[] = [];
    for (const target of targets) {
      // Never unticks: an item already ticked (elsewhere, or by a repeated click) stays as it is.
      const result = await run(() => store.markCheckinDone(target.itemId, target.occursAt));
      if (!result.ok || result.value === null) continue;
      logReminderDecision(store, ReminderDecision.Done, [target.key]);
      const item = liveOnly(await queries.checkinItems(store.repo)).find(
        (entry) => entry.id === target.itemId,
      );
      if (item !== undefined) names.push(item.data.name);
    }
    if (names.length > 0)
      showUndo(t('reminder.doneToast', { names: names.join(t('reminder.itemSeparator')) }));
  };
  useEffect(() => {
    handleRef.current = handle;
  });

  useEffect(() => {
    const handle = (command: ReminderCommand | null): void => {
      if (command !== null) void handleRef.current(command);
    };
    // A click that found no page opened this one with the command in the query.
    const fromQuery = readReminderQuery(window.location.search);
    if (fromQuery !== null) {
      const url = new URL(window.location.href);
      url.searchParams.delete(REMINDER_QUERY);
      url.searchParams.delete(REMINDER_KEYS_QUERY);
      window.history.replaceState(window.history.state, '', url);
      handle(fromQuery);
    }
    const container = navigator.serviceWorker as ServiceWorkerContainer | undefined;
    const onMessage = (event: MessageEvent): void => handle(readReminderMessage(event.data));
    container?.addEventListener('message', onMessage);
    // Another tab turned the device switch on or off.
    const onStorage = (event: StorageEvent): void => {
      if (event.key === StorageKey.ReminderEnabled) useReminderPrefs.getState().reload();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      container?.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    // Without permission (never granted, or revoked in the browser settings) nothing is checked,
    // so no reminder is recorded as shown; the settings card then shows the switch off.
    if (!enabled || notifyPermission() !== NotifyPermission.Granted) return;
    const runner = createReminderRunner({
      ...reminderStorageDeps(store),
      now,
      writeState: writeStoredReminderState,
      notify: async (plan, at) => {
        const failed: string[] = [];
        for (const notice of noticesOf(plan, at, t)) {
          const shown = await showReminderNotice(notice, (command) => void handleRef.current(command));
          if (!shown) failed.push(...notice.keys);
        }
        if (failed.length > 0) console.warn('[reminder] notifications could not be shown; retrying', failed.length);
        logPlan(store, plan, failed);
        return failed;
      },
      withLock: withReminderLock,
      setTimer: (handler, ms) => window.setTimeout(handler, ms),
      clearTimer: (handle) => window.clearTimeout(handle),
    });
    runnerRef.current = runner;
    runner.start();
    // Timers of a hidden page may have been paused (sleep, frozen tab): check when it shows again.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void runner.check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      runner.stop();
      runnerRef.current = null;
    };
  }, [enabled, store, now, t]);
}
